import { FC, useContext } from 'react';
import { Button, Badge } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { useSnapshot } from 'valtio';
import { formStore } from '../../../store';
import { SidebarControlContext } from '../../../providers/SidebarControlContext';

/**
 * Button component for accessing form reminders management.
 *
 * Displays a bell icon button in the form control bar that opens the sidebar
 * reminders view for managing scheduled notifications.
 * Only renders when a form is loaded.
 *
 * Subscribes to two leaves, `form.data.docId` (present exactly while the form
 * document is loaded) and `form.data.reminders`. `useFormStore()` is banned here:
 * it spreads `formVersion.data`, which re-rendered this button on every keystroke
 * in the form even though reminders live on the form document (SYD-2252).
 *
 * @returns JSX element containing button, or null if no form selected
 */
export const FormRemindersButton: FC = () => {
  const formSnap = useSnapshot(formStore.state).form;
  const sidebarControl = useContext(SidebarControlContext);

  const formDocId = formSnap.data?.docId;
  const reminders = formSnap.data?.reminders || {};
  const triggeredCount = Object.values(reminders).filter(
    (reminder) => reminder.triggered,
  ).length;

  const handleOpenSidebar = () => {
    if (sidebarControl) {
      sidebarControl.openRemindersSidebar();
    }
  };

  if (!formDocId) {
    return null;
  }

  return (
    <Badge count={triggeredCount} color="blue" showZero={false}>
      <Button
        type="text"
        icon={<ClockCircleOutlined style={{ fontSize: 19 }} />}
        onClick={handleOpenSidebar}
        aria-label="Manage form reminders"
      />
    </Badge>
  );
};
